import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Calendar, Heart } from "@phosphor-icons/react";

export default function PreConception() {
  const [cycles, setCycles] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ cycle_start: "", cycle_length: 28, period_length: 5, notes: "" });

  const load = async () => { const r = await api.get("/prep/preconception/cycles"); setCycles(r.data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/prep/preconception/cycle", { ...form, cycle_length: Number(form.cycle_length), period_length: Number(form.period_length) });
      setForm({ cycle_start: "", cycle_length: 28, period_length: 5, notes: "" });
      setAdding(false);
      load();
      toast.success("Cycle logged.");
    } catch { toast.error("Could not save."); }
  };

  const current = cycles[0];

  return (
    <div className="space-y-8" data-testid="preconception-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline text-saffron mb-2">Planning to conceive</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl">Fertility companion</h1>
          <p className="text-ink-700 mt-2">A gentle compass for the months before pregnancy.</p>
        </div>
        <button onClick={() => setAdding(!adding)} className="btn-primary" data-testid="add-cycle-btn"><Plus weight="thin" size={18} /> Log cycle</button>
      </header>

      {current && (
        <div className="grid lg:grid-cols-3 gap-6" data-testid="current-cycle">
          <div className="vansha-card p-6 lg:col-span-2 bg-forest text-white">
            <p className="overline" style={{ color: "#D9C9B4" }}>Fertility window</p>
            <p className="font-serif text-4xl mt-2 leading-snug">{current.fertility_window_start} <span className="opacity-60 text-2xl">→</span> {current.fertility_window_end}</p>
            <p className="opacity-80 mt-3">Predicted ovulation: <b>{current.ovulation_date}</b></p>
          </div>
          <div className="vansha-card p-6">
            <Heart weight="thin" size={28} className="text-terracotta mb-2" />
            <h3 className="font-serif text-xl">Preconception checklist</h3>
            <ul className="text-sm text-ink-700 mt-3 space-y-2">
              <li>· Start folic acid (400 mcg)</li>
              <li>· Schedule preconception check-up</li>
              <li>· Track cycle for 3 months</li>
              <li>· Reduce caffeine, stop alcohol</li>
              <li>· Get to a healthy weight together</li>
            </ul>
          </div>
        </div>
      )}

      {adding && (
        <form onSubmit={submit} className="vansha-card p-6 grid sm:grid-cols-3 gap-5" data-testid="cycle-form">
          <div><label className="overline mb-2 block">Last period start</label><input type="date" required value={form.cycle_start} onChange={(e) => setForm({ ...form, cycle_start: e.target.value })} className="input-line" data-testid="cycle-start" /></div>
          <div><label className="overline mb-2 block">Cycle length (days)</label><input type="number" value={form.cycle_length} onChange={(e) => setForm({ ...form, cycle_length: e.target.value })} className="input-line" data-testid="cycle-length" /></div>
          <div><label className="overline mb-2 block">Period length</label><input type="number" value={form.period_length} onChange={(e) => setForm({ ...form, period_length: e.target.value })} className="input-line" data-testid="period-length" /></div>
          <div className="sm:col-span-3 flex gap-3 justify-end">
            <button type="button" onClick={() => setAdding(false)} className="btn-ghost">Cancel</button>
            <button className="btn-primary" data-testid="cycle-submit">Save</button>
          </div>
        </form>
      )}

      <section>
        <h2 className="font-serif text-2xl mb-4">Cycle history</h2>
        {cycles.length === 0 ? <p className="text-ink-500" data-testid="cycles-empty">Log your first cycle above.</p> : (
          <div className="space-y-3">
            {cycles.map((c) => (
              <div key={c.id} className="vansha-card p-5 flex items-center gap-4" data-testid={`cycle-${c.id}`}>
                <Calendar weight="thin" size={22} className="text-terracotta" />
                <div className="flex-1">
                  <p className="font-medium">Cycle from {c.cycle_start}</p>
                  <p className="text-sm text-ink-500">Ovulation: {c.ovulation_date} · Length: {c.cycle_length}d</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
